import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  UsersRound, Plus, Pencil, Trash2, UserPlus, UserMinus,
  Search, Shield, Phone, Briefcase
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const TEAM_TYPES = [
  { value: "telecaller", label: "Telecaller", icon: Phone, color: "bg-violet-100 text-violet-700" },
  { value: "sales", label: "Sales", icon: Briefcase, color: "bg-blue-100 text-blue-700" },
  { value: "cp", label: "Channel Partner", icon: UsersRound, color: "bg-amber-100 text-amber-700" },
  { value: "relationship", label: "Relationship", icon: Shield, color: "bg-emerald-100 text-emerald-700" },
];

const emptyForm = { name: "", team_type: "sales", project_mapping: "", team_leader: "", max_capacity: "" };

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [newMemberId, setNewMemberId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const fetchData = async () => {
    try {
      const [teamsRes, usersRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/auth/users`).catch(() => ({ data: [] })),
      ]);
      setTeams(teamsRes.data);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (err) {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingTeam(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (team) => {
    setEditingTeam(team);
    setForm({
      name: team.name || "",
      team_type: team.team_type || "sales",
      project_mapping: (team.project_mapping || []).join(", "),
      team_leader: team.team_leader || "",
      max_capacity: team.max_capacity || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Team name is required"); return; }
    const payload = {
      name: form.name,
      team_type: form.team_type,
      project_mapping: form.project_mapping ? form.project_mapping.split(",").map(s => s.trim()).filter(Boolean) : [],
      team_leader: form.team_leader || null,
      max_capacity: form.max_capacity ? parseInt(form.max_capacity) : null,
    };
    try {
      if (editingTeam) {
        await axios.put(`${API}/teams/${editingTeam.team_id}`, payload);
        toast.success("Team updated");
      } else {
        await axios.post(`${API}/teams`, payload);
        toast.success("Team created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save team");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team? Members will not be deleted.")) return;
    try {
      await axios.delete(`${API}/teams/${id}`);
      toast.success("Team deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const openMembers = (team) => {
    setSelectedTeam(team);
    setNewMemberId("");
    setMemberDialogOpen(true);
  };

  const addMember = async () => {
    if (!newMemberId) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.team_id}/members`, { user_id: newMemberId });
      toast.success("Member added");
      setNewMemberId("");
      fetchData();
      // refresh team in dialog
      const updated = await axios.get(`${API}/teams/${selectedTeam.team_id}`);
      setSelectedTeam({ ...selectedTeam, ...updated.data });
    } catch (err) {
      toast.error("Failed to add member");
    }
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}/members/${memberId}`);
      toast.success("Member removed");
      fetchData();
      const updated = await axios.get(`${API}/teams/${selectedTeam.team_id}`);
      setSelectedTeam({ ...selectedTeam, ...updated.data });
    } catch (err) {
      toast.error("Failed to remove member");
    }
  };

  const filtered = teams.filter((t) => {
    const matchSearch = t.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || t.team_type === filterType;
    return matchSearch && matchType;
  });

  const getTypeInfo = (v) => TEAM_TYPES.find((t) => t.value === v) || TEAM_TYPES[1];
  const getUserName = (uid) => allUsers.find(u => u.user_id === uid)?.name || uid;

  // Members not already in the selected team
  const availableUsers = allUsers.filter(u =>
    !selectedTeam?.members?.includes(u.user_id)
  );

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="teams-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Teams</h1>
            <p className="text-sm text-slate-500 mt-1">Manage teams, assign members, and map projects</p>
          </div>
          <Button onClick={openCreate} data-testid="create-team-btn">
            <Plus className="w-4 h-4 mr-2" /> New Team
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TEAM_TYPES.map((tt) => {
            const count = teams.filter(t => t.team_type === tt.value).length;
            const Icon = tt.icon;
            return (
              <Card key={tt.value}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tt.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">{tt.label}</p>
                    <p className="text-xl font-bold text-slate-900">{count}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search teams..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10" data-testid="teams-search" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="teams-type-filter">
              <SelectValue placeholder="Team Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TEAM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Team Cards */}
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading teams...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-400">
              <UsersRound className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No teams found. Create your first team.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((team) => {
              const typeInfo = getTypeInfo(team.team_type);
              const Icon = typeInfo.icon;
              const members = team.members_detail || [];
              return (
                <Card key={team.team_id} className="hover:shadow-md transition-shadow" data-testid={`team-card-${team.team_id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{team.name}</CardTitle>
                          <Badge variant="outline" className={`mt-1 text-xs ${typeInfo.color}`}>{typeInfo.label}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(team)} data-testid={`team-edit-${team.team_id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(team.team_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {team.project_mapping?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {team.project_mapping.map((p, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                        ))}
                      </div>
                    )}
                    {team.max_capacity && (
                      <p className="text-xs text-slate-500">Capacity: {team.max_capacity} leads</p>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-slate-600">Members ({members.length})</p>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openMembers(team)}
                          data-testid={`team-manage-members-${team.team_id}`}>
                          <UserPlus className="w-3 h-3 mr-1" /> Manage
                        </Button>
                      </div>
                      {members.length === 0 ? (
                        <p className="text-xs text-slate-400">No members yet</p>
                      ) : (
                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                          {members.map((m) => (
                            <div key={m.user_id} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${m.status === 'available' ? 'bg-emerald-500' : m.status === 'busy' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                                <span className="text-slate-700">{m.name}</span>
                                {team.team_leader === m.user_id && <Badge className="text-[10px] px-1.5 py-0 bg-amber-500 text-white">Lead</Badge>}
                              </div>
                              <span className="text-slate-400">{m.active_leads || 0} leads</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit Team Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="team-dialog">
            <DialogHeader>
              <DialogTitle>{editingTeam ? "Edit Team" : "New Team"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Team Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Telecaller Team A" data-testid="team-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Team Type</label>
                  <Select value={form.team_type} onValueChange={(v) => setForm({ ...form, team_type: v })}>
                    <SelectTrigger data-testid="team-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Max Capacity</label>
                  <Input type="number" value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: e.target.value })}
                    placeholder="e.g. 50" data-testid="team-capacity-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Project Mapping</label>
                <Input value={form.project_mapping} onChange={(e) => setForm({ ...form, project_mapping: e.target.value })}
                  placeholder="Comma-separated: Skyline, Riverview" data-testid="team-projects-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Team Leader</label>
                <Select value={form.team_leader || "_none"} onValueChange={(v) => setForm({ ...form, team_leader: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="team-leader-select"><SelectValue placeholder="Select leader" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No leader</SelectItem>
                    {allUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="team-save-btn">{editingTeam ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Member Management Dialog */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="member-dialog">
            <DialogHeader>
              <DialogTitle>Manage Members — {selectedTeam?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Add Member */}
              <div className="flex gap-2">
                <Select value={newMemberId || "_choose"} onValueChange={(v) => setNewMemberId(v === "_choose" ? "" : v)}>
                  <SelectTrigger className="flex-1" data-testid="add-member-select">
                    <SelectValue placeholder="Select user to add" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_choose">Choose a user...</SelectItem>
                    {availableUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addMember} disabled={!newMemberId} data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>

              {/* Current Members */}
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {(selectedTeam?.members || []).length === 0 ? (
                  <p className="p-4 text-center text-sm text-slate-400">No members in this team</p>
                ) : (
                  (selectedTeam?.members || []).map((uid) => {
                    const u = allUsers.find(x => x.user_id === uid);
                    return (
                      <div key={uid} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u?.name || uid}</p>
                          <p className="text-xs text-slate-500">{u?.email || ""}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeMember(uid)}
                          data-testid={`remove-member-${uid}`}>
                          <UserMinus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TeamsPage;
